import { useEffect, useState } from 'react';
import { api } from '../lib/api';

const POLL_MS = 5000;

const fmtElapsed = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const fmtAgo = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s fa`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h fa`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
};

const TRIGGER_LABEL = {
  nightly: 'Notturno',
  on_demand: 'On-demand',
  manual: 'Manuale',
  backfill: 'Backfill',
};

function StatusDot({ status }) {
  const color = ({
    running: '#2563eb',
    done: '#16a34a',
    failed: '#dc2626',
    queued: '#d97706',
  })[status] || '#737373';
  const pulsing = status === 'running';
  return (
    <span
      title={status}
      style={{
        display: 'inline-block',
        width: 8, height: 8, borderRadius: '50%',
        background: color,
        boxShadow: pulsing ? `0 0 0 0 ${color}` : 'none',
        animation: pulsing ? 'ag-pulse 1.4s ease-out infinite' : 'none',
        verticalAlign: 'middle',
      }}
    />
  );
}

function ProgressBar({ pct }) {
  return (
    <div style={{
      height: 6, borderRadius: 3, background: 'var(--bg-elev)',
      border: '1px solid var(--border)', overflow: 'hidden',
    }}>
      <div style={{
        width: `${pct ?? 0}%`,
        height: '100%',
        background: 'linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)',
        transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

function PvpRunRow({ run }) {
  const isRun = run.status === 'running';
  return (
    <div style={{
      padding: '10px 14px',
      border: '1px solid var(--border)',
      borderRadius: 8,
      background: 'var(--bg-elev)',
      marginBottom: 8,
      minWidth: 0,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
        flexWrap: 'wrap', minWidth: 0,
      }}>
        <StatusDot status={run.status} />
        <span style={{
          fontWeight: 600, fontSize: 13,
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={`${run.center_lat.toFixed(4)}, ${run.center_lng.toFixed(4)}`}>
          {run.location_label || `${run.center_lat.toFixed(3)}, ${run.center_lng.toFixed(3)}`} · r={run.radius_km}km
        </span>
        <span style={{
          fontSize: 10, fontWeight: 500,
          padding: '2px 6px', borderRadius: 4,
          background: 'var(--bg)',
          color: 'var(--fg-muted)',
          border: '1px solid var(--border)',
          flex: '0 0 auto',
        }}>
          {TRIGGER_LABEL[run.trigger] || run.trigger}
        </span>
        <span style={{
          marginLeft: 'auto', fontSize: 11.5, color: 'var(--fg-muted)',
          flex: '0 0 auto', whiteSpace: 'nowrap',
        }}>
          {isRun ? fmtElapsed(run.elapsed_seconds) : fmtAgo(run.ran_at)}
        </span>
      </div>

      {isRun && run.progress_total > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--fg-muted)', marginBottom: 4 }}>
            <span>
              Pagina {run.progress_current}/{run.progress_total}
              {run.progress_pct != null ? ` · ${run.progress_pct}%` : ''}
            </span>
            {run.eta_seconds != null && (
              <span>ETA ~{fmtElapsed(run.eta_seconds)}</span>
            )}
          </div>
          <ProgressBar pct={run.progress_pct} />
        </>
      )}

      {isRun && run.progress_total === 0 && (
        <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          Inizializzazione… (calcolando il totale pagine)
        </div>
      )}

      {!isRun && (
        <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
          {run.items_imported} immobili importati
          {run.status === 'failed' && run.error_message && (
            <span style={{ color: '#dc2626', marginLeft: 8 }}> · {run.error_message}</span>
          )}
        </div>
      )}
    </div>
  );
}

function AstemasterJobRow({ job }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px',
      border: '1px solid var(--border)', borderRadius: 6,
      background: 'var(--bg-elev)', marginBottom: 6,
      minWidth: 0,
    }}>
      <StatusDot status={job.status} />
      <span style={{ fontSize: 12, fontWeight: 500, flex: '0 0 auto', color: 'var(--fg-muted)' }}>
        #{job.id}
      </span>
      <span style={{
        fontSize: 12.5, flex: '1 1 0', minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {job.comune ? `${job.comune} — ` : ''}{job.indirizzo || `imm ${job.immobile_id}`}
      </span>
      <span style={{
        fontSize: 11, color: 'var(--fg-muted)',
        fontVariantNumeric: 'tabular-nums',
        flex: '0 0 auto', whiteSpace: 'nowrap',
      }}>
        {job.status === 'running'
          ? fmtElapsed(job.elapsed_seconds)
          : `in coda da ${fmtAgo(job.created_at)}`}
      </span>
    </div>
  );
}

export default function Agent() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get('/api/agent/status');
        if (alive) { setData(data); setError(null); }
      } catch (e) {
        if (alive) setError(e?.message || 'Errore');
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <main className="content content--inbox">
      <style>{`
        @keyframes ag-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.6); }
          70%  { box-shadow: 0 0 0 8px rgba(37, 99, 235, 0); }
          100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
        }
      `}</style>

      <div className="page__header">
        <div className="page__title-wrap">
          <h1 className="page__title">Agent</h1>
          <p className="page__sub">
            Monitoraggio scansioni PVP e analisi Rivelasta in tempo reale (refresh ogni 5s)
          </p>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16,
          background: 'oklch(0.96 0.04 25)',
          border: '1px solid oklch(0.75 0.12 25)',
          color: 'oklch(0.35 0.15 25)',
          borderRadius: 8, fontSize: 13,
        }}>{error}</div>
      )}

      <div style={{
        display: 'grid',
        // min(380px, 100%) consente alla colonna di stringersi sotto i 380px
        // quando il contenitore è più piccolo (mobile), evitando overflow orizzontale
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))',
        gap: 20,
        minWidth: 0,
      }}>
        {/* ====== PVP ====== */}
        <section style={{
          padding: 20,
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 12,
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            Scansioni PVP
            {data?.pvp?.running?.length > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 500,
                padding: '2px 8px', borderRadius: 999,
                background: '#dbeafe', color: '#1e40af',
              }}>{data.pvp.running.length} in corso</span>
            )}
          </h2>

          {loading && !data && <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Caricamento…</div>}

          {data?.pvp?.running?.length === 0 && (
            <div style={{ color: 'var(--fg-muted)', fontSize: 12, marginBottom: 12 }}>
              Nessuna scansione in corso
            </div>
          )}

          {data?.pvp?.running?.map((r) => <PvpRunRow key={r.id} run={r} />)}

          {data?.pvp?.recent?.length > 0 && (
            <>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: 'var(--fg-muted)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                margin: '14px 0 8px',
              }}>
                Storico recente
              </div>
              {data.pvp.recent.map((r) => <PvpRunRow key={r.id} run={r} />)}
            </>
          )}
        </section>

        {/* ====== ASTEMASTER ====== */}
        <section style={{
          padding: 20,
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 12,
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            Analisi Rivelasta
            <span style={{
              fontSize: 11, fontWeight: 500,
              padding: '2px 8px', borderRadius: 999,
              background: '#fef3c7', color: '#92400e',
            }}>{data?.astemaster?.queued_total ?? 0} in coda</span>
          </h2>

          {loading && !data && <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>Caricamento…</div>}

          {data?.astemaster?.running?.length > 0 && (
            <>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: 'var(--fg-muted)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                margin: '0 0 6px',
              }}>
                In esecuzione ({data.astemaster.running.length})
              </div>
              {data.astemaster.running.map((j) => <AstemasterJobRow key={j.id} job={j} />)}
            </>
          )}

          {data?.astemaster?.queued?.length > 0 && (
            <>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: 'var(--fg-muted)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                margin: '14px 0 6px',
              }}>
                Prossime in coda
              </div>
              {data.astemaster.queued.map((j) => <AstemasterJobRow key={j.id} job={j} />)}
              {data.astemaster.queued_total > data.astemaster.queued.length && (
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', textAlign: 'center', marginTop: 4 }}>
                  …e altre {data.astemaster.queued_total - data.astemaster.queued.length} in coda
                </div>
              )}
            </>
          )}

          {(data?.astemaster?.running?.length === 0 && data?.astemaster?.queued?.length === 0) && (
            <div style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
              Nessuna analisi attiva
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
